'use client'

import { Container } from '@/components/container'
import { SectionHeader } from '@/components/section-header'
import { Button } from '@/components/button'
import { MotionDiv } from '@/components/motion-wrapper'
import { useEffect, useRef } from 'react'
import { trackEvent } from '@/lib/utils'
import { 
  DocumentTextIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'

import { getFeaturedBlogPosts } from '@/lib/blog-data'
import Link from 'next/link'

const iconMap: Record<string, React.ReactNode> = {
  'MVP Development': <RocketLaunchIcon className="h-6 w-6" />,
  'SaaS': <ChartBarIcon className="h-6 w-6" />,
  'Architecture': <LightBulbIcon className="h-6 w-6" />,
  'AI Integration': <DocumentTextIcon className="h-6 w-6" />,
}

export function ResourcesSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const hasTrackedView = useRef(false)
  const resources = getFeaturedBlogPosts()

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedView.current) {
            hasTrackedView.current = true
            trackEvent('section_viewed', {
              event_category: 'engagement',
              event_label: 'resources_section',
              value: 1,
            })
          }
        })
      },
      { threshold: 0.5 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  return (
    <section ref={sectionRef} id="resources" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <Container>
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionHeader
            subheading="Resources & Insights"
            heading="Learn from Our Experience"
            description="Practical guides, insights, and best practices from our experience building MVPs and SaaS products."
          />
        </MotionDiv>

        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-12" />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {resources.map((resource, index) => {
            const icon = iconMap[resource.category] || <DocumentTextIcon className="h-6 w-6" />
            return (
              <MotionDiv
                key={resource.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link 
                  href={`/blog/${resource.slug}`}
                  className="group block"
                  onClick={() => {
                    trackEvent('blog_post_clicked', {
                      event_category: 'engagement',
                      event_label: resource.slug,
                      value: 1,
                    })
                  }}
                >
                  <div className="group relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-lg transition-all hover:border-gray-300 h-full flex flex-col">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 p-3 rounded-lg bg-gray-100 text-gray-700 group-hover:bg-gray-200 transition-colors">
                        {icon}
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            {resource.category}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-400">{resource.readTime}</span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                          {resource.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {resource.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              </MotionDiv>
            )
          })}
        </div>

        <div className="mt-12 text-center">
          <Button
            variant="outline"
            href="/blog"
            onClick={() => {
              trackEvent('view_all_blog_clicked', {
                event_category: 'engagement',
                event_label: 'resources_section',
                value: 1,
              })
            }}
          >
            View All Blog Posts
          </Button>
        </div>
      </Container>
    </section>
  )
}

